<template lang="pug">
.flex-col
  el-select(
    v-model="model",
    v-cancel-read-only,
    :clearable="clearable",
    filterable,
    remote,
    :remote-method="filter",
    :multiple="props.multiple",
    :collapse-tags="collapseTags",
    :max-collapse-tags="3",
    :placeholder="props.placeholder",
    placement="bottom",
    suffix-icon="ArrowDown",
    :remote-show-suffix="true",
    :disabled="props.disabled"
  )
    template(#prefix)
      font-awesome-icon(:icon="['fas', 'fa-magnifying-glass']")
    template(#empty) {{ $t("noData") }}
    el-option-group(v-lazy-load="loadMoreData")
      el-option(
        v-for="item in resultWithTemplateNames",
        :key="item.uuid",
        :value="item.uuid",
        :label="getLabel(item)"
      )
      el-option(v-if="loading", v-loading="true", :value="''", label="")
      el-button.w-full(
        v-if="canSelectAll",
        :loading="selectingAll",
        type="default",
        @click.stop="selectAllResults"
      ) {{ $t("selectAll") }} ({{ page.total }})

  TwinDetailsCompact.mt-2(
    v-if="showDetails && selectedItem",
    :id="selectedItem.uuid",
    @twin="(t) => emit('twinSelected', t)"
  )
</template>

<script setup lang="ts">
import {
  TwinManagement,
  type TwinManagementTypes,
} from "@inctec/smx-ui-client";
import { computed, onMounted, ref, watch } from "vue";
import vLazyLoad from "../../../directives/elSelectInfiniteScroll";
import { debounce } from "lodash";
import { useTwinStore } from "../../../stores/twinManagement";
import { cancelReadOnly as vCancelReadOnly } from "@shared/directives/select-readonly";
import TwinDetailsCompact from "./TwinDetailsCompact.vue";

type Twin = TwinManagementTypes.TwinResponse;
type TwinBase = Pick<Twin, "uuid" | "name"> & { templateId: string };
type SelectableTwin = Twin | TwinBase;
type TwinQuery = TwinManagementTypes.SearchTwinsData["body"];

const props = withDefaults(
  defineProps<{
    query: TwinQuery;
    modelValue?: Array<SelectableTwin | string> | null;
    multiple?: boolean;
    placeholder?: string;
    // emit an array of ids
    emitIds?: boolean;
    showDetails?: boolean;
    // Show details of selected twins, usually this element will use a reduced twin dataset `TwinBase` since it was created to select Twin Relations
    // In some cases more details are needed, if details is set, modelValue will be seen as a `TwinResponse`.
    // Note that TwinResponse and TwinBase can't be used interchangeably, since the backend uses different property names (templateId and templateUuid)
    details?: boolean;
    displayTemplateName?: boolean;
    // Properties to be shown in the title of the selection, used to differentiate items with the same name
    // Array of property customIds
    propsInLabel?: string[] | null;
    disabled?: boolean;
    clearable?: boolean;
    selectAll?: boolean;
    collapseTags?: boolean;
  }>(),
  {
    modelValue: null,
    emitIds: false,
    placeholder: undefined,
    clearable: true,
    propsInLabel: null,
    selectAll: false,
  },
);

const result = ref<SelectableTwin[]>([]);

const selectedTwins = ref<SelectableTwin[]>([]);

const page = ref<{ total: number | null; pagesTotal: number | null }>({
  total: null,
  pagesTotal: null,
});
// NOTE: all data loading is happening in this component itself, since we need to support infinite scrolling and search across an arbitrary number of input field that are being rendered to the user
onMounted(() => {
  void loadMoreData();
  // If data is set as twins, we can use them directly
  if (
    props.modelValue &&
    props.modelValue.length > 0 &&
    typeof props.modelValue[0] !== "string"
  ) {
    selectedTwins.value = props.modelValue as SelectableTwin[];
  } else {
    void loadSelectedTwins();
  }
});

watch(
  () => props.modelValue,
  () => {
    if (
      props.modelValue &&
      props.modelValue.length > 0 &&
      typeof props.modelValue[0] !== "string"
    ) {
      selectedTwins.value = props.modelValue as SelectableTwin[];
      setResult(result.value);
    } else {
      void loadSelectedTwins();
    }
  },
);

// twinSelected is emitted if `showDetails` is true, in which case the twin is loaded after selection and then emitted with all data
const emit = defineEmits(["update:modelValue", "twinSelected"]);

const pagination = ref({
  page: 0,
  pageSize: 10,
});

const request = ref<TwinQuery>({
  showProperties: props.propsInLabel !== null || props.details,
});

const loading = ref(false);
const loaded = ref(false);
const error = ref(false);
const selectingAll = ref(false);

const loadSearch = ref();

let abortController: AbortController | null = null;

async function loadMoreData(reload?: boolean) {
  // NOTE: if we already load the current search query, do not reload
  if (
    !reload &&
    request.value.search === loadSearch.value &&
    (loading.value || (loaded.value && !hasMorePages.value))
  ) {
    return;
  }
  // NOTE: search queries might take time depending on complexity, we have to make sure that we actually only retrieve the last request, thus prior requests have to be cancelled
  if (abortController !== null) {
    abortController.abort();
    abortController = null;
  }
  const query = { ...props.query, ...request.value };
  loadSearch.value = request.value.search;
  loaded.value = false;
  loading.value = true;
  pagination.value.page++;
  abortController = new AbortController();
  const response = await TwinManagement.searchTwins({
    body: query,
    query: pagination.value,
    signal: abortController.signal,
  }).catch((e) => {
    if (e && e?.code === "ERR_CANCELED") {
      // We cancelled the request in favor of a new search query
      e.dontLog = true;
      throw e;
    } else {
      error.value = true;
      loading.value = false;
      loaded.value = true;
    }
    throw e;
  });
  // NOTE: if we load the first page of a search, we want to override all previous data.
  // This is important because a request might finish between being cancelled and a data reset by a new request, in which case its data will be in the result.
  // In case of a next page load, we have to merge the data
  if (pagination.value.page === 1) {
    setResult(mapResponse(response.data.result ?? []));
  } else {
    setResult([...result.value, ...mapResponse(response.data.result ?? [])]);
  }
  page.value = {
    total: response.data.total ?? null,
    pagesTotal: response.data.pagesTotal ?? null,
  };
  loading.value = false;
  loaded.value = true;
}

function setResult(twins: SelectableTwin[]) {
  // Filter for uniqueness, note that this isn't very effective but okay for human possible scroll sizes, this makes sense since there might be items created while scrolling, resulting in differences in paging
  const ids = new Set();
  result.value = [...twins, ...selectedTwins.value].filter((item) => {
    if (!ids.has(item.uuid)) {
      ids.add(item.uuid);
      return true;
    }
    return false;
  });
}

function mapResponse(twins: Twin[]): SelectableTwin[] {
  if (props.details || props.showDetails || props.propsInLabel) {
    return twins;
  }
  return twins.map((item) => ({
    uuid: item.uuid,
    name: item.name,
    templateId: item.templateUuid ?? "",
  }));
}

async function loadSelectedTwins() {
  // Load the data of selected twins to show proper names, if set as trings, otherwise the data does not have to be loaded
  if (
    props.modelValue &&
    props.modelValue.length > 0 &&
    typeof props.modelValue[0] === "string"
  ) {
    const uuids = props.modelValue as string[];
    const response = await TwinManagement.searchTwins({
      body: { uuids, showProperties: props.propsInLabel !== null },
      query: { page: 1, pageSize: 50 },
    });
    selectedTwins.value = (response.data.result ?? []).map((item) => ({
      uuid: item.uuid,
      name: item.name,
      templateId: item.templateUuid ?? "",
    }));
    setResult(result.value);
  }
}

watch(
  () => props.query,
  (oldVal, newVal) => {
    // NOTE: this isn't pretty, but it avoids unwanted reloaded if the query object hasn't changed at all
    if (JSON.stringify(oldVal) === JSON.stringify(newVal)) {
      return;
    }
    pagination.value.page = 0;
    setResult([]);
    page.value = { total: null, pagesTotal: null };
    loading.value = true;
    void loadMoreData(true);
  },
);

const debouncedLoadData = debounce(loadMoreData, 300);

const search = ref<string>("");

watch(search, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    if (newValue !== "") {
      // reset the current data
      request.value.search = newValue;
      pagination.value.page = 0;
      setResult([]);
      page.value = { total: null, pagesTotal: null };
      loading.value = true;
      void debouncedLoadData();
    } else {
      // NOTE: backend search now return NO values if search is ""
      request.value.search = undefined;
      pagination.value.page = 0;
      setResult([]);
      page.value = { total: null, pagesTotal: null };
      loading.value = true;
      void loadMoreData();
    }
  }
});

function filter(input: string) {
  search.value = input;
}

const twinStore = useTwinStore();

const resultWithTemplateNames = computed(() => {
  if (props.displayTemplateName && twinStore.templatesLoadingState.loaded) {
    const templates = twinStore.templatesLoadingState.result ?? [];
    return result.value.map((item) => {
      const templateId =
        "templateId" in item ? item.templateId : item.templateUuid;
      const template = templates.find(
        (template) => template.uuid === templateId,
      );
      return { ...item, templateName: template?.name ?? "" };
    });
  }
  return result.value;
});

const hasMorePages = computed(() => {
  if (page.value.pagesTotal === null) {
    // inital load
    return true;
  }
  if (page.value !== null) {
    return page.value.pagesTotal > pagination.value.page;
  }
  return false;
});

const canSelectAll = computed(
  () =>
    props.selectAll &&
    props.multiple &&
    page.value.total !== null &&
    page.value.total > 0 &&
    page.value.total < 50,
);

const collapseTags = computed(
  () =>
    props.collapseTags && props.multiple && (props.modelValue?.length ?? 0) > 5,
);

const model = computed({
  get() {
    if (props.modelValue && props.modelValue.length > 0) {
      if (typeof props.modelValue[0] === "string") {
        return props.multiple ? props.modelValue : props.modelValue[0];
      }
      const twins = props.modelValue as SelectableTwin[];
      return props.multiple
        ? twins.map((item) => item.uuid)
        : (twins[0]?.uuid ?? null);
    } else {
      return null;
    }
  },
  set(value) {
    if (value !== undefined && value !== null) {
      if (props.emitIds) {
        emit("update:modelValue", Array.isArray(value) ? value : [value]);
        return;
      }

      if (typeof value === "string") {
        const resultItem = result.value.find((result) => result.uuid === value);
        if (resultItem) {
          emit("update:modelValue", [resultItem]);
        } else {
          emit("update:modelValue", undefined);
        }
        return;
      }
      emit(
        "update:modelValue",
        value.map((item) =>
          result.value.find((result) => result.uuid === item),
        ),
      );
    } else {
      emit("update:modelValue", undefined);
    }
  },
});

async function selectAllResults() {
  selectingAll.value = true;
  try {
    const query = { ...props.query, ...request.value };
    const queryKey = JSON.stringify(query);
    const response = await TwinManagement.searchTwins({
      body: query,
      query: { page: 1, pageSize: 50 },
    });
    if (queryKey !== JSON.stringify({ ...props.query, ...request.value })) {
      return;
    }
    const twins = mapResponse(response.data.result ?? []);
    setResult(twins);
    page.value = {
      total: response.data.total ?? null,
      pagesTotal: response.data.pagesTotal ?? null,
    };
    model.value = twins.map((twin) => twin.uuid);
  } finally {
    selectingAll.value = false;
  }
}

function getLabel(twin: SelectableTwin & { templateName?: string }) {
  let label = "";

  if (props.displayTemplateName) {
    label += (twin.templateName ?? "") + " ";
  }

  label += twin.name;

  if (props.propsInLabel && "properties" in twin) {
    const labelDetails = props.propsInLabel
      .map((customId) => {
        const value = twin.properties?.find(
          (prop) => prop.customId === customId,
        )?.propertyValue?.value;
        return typeof value === "string" || typeof value === "number"
          ? String(value)
          : undefined;
      })
      .filter((item): item is string => item !== undefined && item !== "");
    label = [label, ...labelDetails].join(", ");
  }
  return label;
}
// Show details
const selectedItem = computed(() =>
  result.value.find((r) => r.uuid === model.value),
);
</script>
